import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Modal, Form, Message } from "semantic-ui-react";
import { useEditDraft } from "../../hooks";

export const EmbargoSettings = ({ draft }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [embargoData, setEmbargoData] = useState({});
  const [error, setError] = useState({ message: "", type: "none" });

  const { mutate: editDraft, isLoading: isEditing } = useEditDraft();

  useEffect(() => {
    initializeEmbargoData();
  }, [draft]);

  const initializeEmbargoData = () => {
    setEmbargoData({
      until: draft.access.embargo.until || "",
      reason: draft.access.embargo.reason || "",
      active: draft.access.embargo.active || false,
    });
  };

  const cleanErrors = () => setError({ message: "", type: "none" });

  const handleInputChange = (e, { name, value }) => {
    setEmbargoData((prev) => ({ ...prev, [name]: value }));
    cleanErrors();
  };

  const validateEmbargoData = () => {
    const today = new Date();
    const maxDate = new Date(today.setMonth(today.getMonth() + 6));
    const selectedDate = new Date(embargoData.until);

    if (
      !embargoData.until ||
      selectedDate < new Date() ||
      selectedDate > maxDate
    ) {
      setError({
        message:
          "Please provide a valid date within the allowed range (maximum 6 months in the future).",
        type: "date",
      });
      return false;
    }

    return true;
  };

  const handleEditWithEmbargo = async (data) => {
    const updatedDraft = {
      ...draft,
      access: {
        ...draft.access,
        embargo: {
          active: data.active,
          until: data.until,
          reason: data.reason,
        },
        files: data.files ? "restricted" : "public",
      },
    };
    await editDraft(updatedDraft);
    setEmbargoData(data);
  };

  const handleSubmit = () => {
    if (!validateEmbargoData()) return;

    const updatedData = { ...embargoData, active: true, files: true }; // Always set files to true
    handleEditWithEmbargo(updatedData);
    setModalOpen(false);
  };

  const handleRemoveEmbargo = () => {
    const updatedData = {
      active: false,
      until: "",
      reason: "",
      files: false, // Always set files to false
    };
    handleEditWithEmbargo(updatedData);
    setConfirmModalOpen(false);
  };

  const renderErrorMessage = () =>
    error.type !== "none" && (
      <Message negative>
        <p>{error.message}</p>
      </Message>
    );

  const renderEmbargoDetails = () => (
    <Message error>
      <Message.Header>Active Embargo</Message.Header>
      <p>
        <strong>Until:</strong> {embargoData.until}
      </p>
      <p>
        <strong>Reason:</strong> {embargoData.reason || "No reason provided"}
      </p>
      <p>WARNING: After publication, embargo cannot be changed!</p>
      <Button
        color="red"
        loading={isEditing}
        onClick={() => setConfirmModalOpen(true)}
      >
        Remove Embargo
      </Button>
      <Button
        color="blue"
        loading={isEditing}
        onClick={() => setModalOpen(true)}
      >
        Edit Embargo
      </Button>
    </Message>
  );

  const renderNoEmbargoMessage = () => (
    <Message positive>
      <Message.Header>No Active Embargo</Message.Header>
      <p>Files will be available after publication.</p>
      <Button
        color="green"
        loading={isEditing}
        onClick={() => setModalOpen(true)}
      >
        Create Embargo
      </Button>
    </Message>
  );

  return (
    <div>
      {embargoData.active ? renderEmbargoDetails() : renderNoEmbargoMessage()}

      <Modal
        open={isModalOpen}
        onClose={() => {
          setModalOpen(false);
          cleanErrors();
        }}
        style={{ maxWidth: "600px" }}
      >
        <Modal.Header>
          {embargoData.active ? "Edit Embargo" : "Create Embargo"}
        </Modal.Header>
        <Modal.Content>
          <Form>
            {renderErrorMessage()}
            <Form.Input
              label="Embargo Until"
              type="date"
              name="until"
              value={embargoData.until}
              onChange={handleInputChange}
              error={error.type === "date"}
              min={new Date().toISOString().split("T")[0]}
              max={
                new Date(new Date().setMonth(new Date().getMonth() + 6))
                  .toISOString()
                  .split("T")[0]
              }
            />
            <Form.TextArea
              label="Reason"
              name="reason"
              value={embargoData.reason}
              onChange={handleInputChange}
            />
          </Form>
          <Message
            warning
            header="Warning"
            content="The embargo affects the availability of files after publication. They won't be available until the embargo is lifted for not community users."
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setModalOpen(false)} loading={isEditing}>
            Cancel
          </Button>
          <Button color="blue" onClick={handleSubmit} loading={isEditing}>
            {embargoData.active ? "Save Changes" : "Create Embargo"}
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={isConfirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
      >
        <Modal.Header>Confirm Remove Embargo</Modal.Header>
        <Modal.Content>
          <p>Are you sure you want to remove the embargo?</p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setConfirmModalOpen(false)}
            loading={isEditing}
          >
            Cancel
          </Button>
          <Button color="red" onClick={handleRemoveEmbargo} loading={isEditing}>
            Confirm
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

EmbargoSettings.propTypes = {
  draft: PropTypes.object.isRequired,
};
